import React from "react";
import LegalLayout from "./LegalLayout";

export default function Privacy() {
  return (
    <LegalLayout
      eyebrow="Legal"
      title="Privacy Policy"
      intro="How GetJewelDesigns collects, uses and protects your personal information."
      lastUpdated="20 July 2026"
      path="/privacy-policy"
    >
      <p>
        This Privacy Policy explains how <strong>Sonam Patel</strong>, a sole proprietor trading as
        "GetJewelDesigns" and based in Surat, Gujarat, India ("GetJewelDesigns", "we"), handles personal
        information when you use our Platform. We are the data controller for the information described
        here, and we process personal data in accordance with India's Digital Personal Data Protection
        Act, 2023 and other applicable law.
      </p>

      <h2>1. Information we collect</h2>
      <ul>
        <li><strong>Account information</strong> — name, email address, role, and, for design specialists, application and profile details.</li>
        <li><strong>Project information</strong> — briefs, references, uploaded files, messages and the deliverables produced.</li>
        <li><strong>Transaction information</strong> — quotations, orders, amounts and payment status. Card details are handled by our payment provider; we do not store full card numbers.</li>
        <li><strong>Technical and usage data</strong> — device, browser, IP address, pages viewed and interactions, collected through cookies and analytics (see section 5).</li>
      </ul>

      <h2>2. How we use your information</h2>
      <ul>
        <li>To operate the Platform: create accounts, prepare quotations, process orders and deliver files.</li>
        <li>To maintain our anonymised model by moderating messages and files for contact-information sharing.</li>
        <li>To communicate with you about your account, quotations, orders and support requests.</li>
        <li>To keep the Platform secure, prevent fraud and abuse, and comply with legal obligations.</li>
        <li>To improve the Platform and, where permitted, for marketing our own services.</li>
      </ul>

      <h2>3. Legal bases</h2>
      <p>
        Where applicable law requires a legal basis, we rely on: performance of our contract with you;
        our legitimate interests in operating and securing the Platform; your consent (for example,
        non-essential analytics); and compliance with legal obligations.
      </p>

      <h2>4. Sharing your information</h2>
      <p>We share personal information only as needed:</p>
      <ul>
        <li><strong>Within the Platform's model</strong> — clients and design specialists never receive each other's identity or contact details. Briefs and deliverables are routed in an anonymised form.</li>
        <li><strong>Service providers (sub-processors)</strong> — hosting and database, email delivery, file storage, product analytics and our payment processor <strong>Razorpay</strong> (which processes your payment and, for that purpose, is an independent controller of your payment data under its own privacy policy), each acting on our instructions or as required to complete your transaction.</li>
        <li><strong>Legal and safety</strong> — where required by law, to enforce our terms, or to protect rights and safety.</li>
        <li><strong>Business transfers</strong> — in connection with a merger, acquisition or asset sale, subject to this policy.</li>
      </ul>
      <p>We do not sell your personal information.</p>

      <h2>5. Cookies and analytics</h2>
      <p>
        We use cookies that are strictly necessary for sign-in and security, and analytics/product tools
        (including <strong>PostHog</strong>) that may record usage and session interactions to help us
        understand and improve the Platform. You can control non-essential cookies through your browser
        and, where offered, our cookie controls. Disabling necessary cookies may break sign-in.
      </p>

      <h2>6. Data retention</h2>
      <p>
        We keep personal information for as long as your account is active and as needed to provide the
        service, then for the period required to meet legal, accounting, tax and dispute-resolution
        obligations, after which it is deleted or anonymised.
      </p>

      <h2>7. Security</h2>
      <p>
        We use technical and organisational measures such as encryption in transit, access controls,
        signed time-limited file links and audit logging. No system is perfectly secure, but we work to
        protect your information and to notify you of significant incidents where required.
      </p>

      <h2>8. Your rights</h2>
      <p>
        Depending on where you live, you may have rights to access, correct, delete, port or restrict
        your personal information, to object to certain processing, and to withdraw consent. To exercise
        these rights, contact <a href="mailto:getjeweldesigns@gmail.com">getjeweldesigns@gmail.com</a>. You
        may also have the right to complain to your local data-protection authority.
      </p>

      <h2>9. International transfers</h2>
      <p>
        Your information may be processed in countries other than your own. Where we transfer data across
        borders, we use appropriate safeguards as required by law.
      </p>

      <h2>10. Children</h2>
      <p>The Platform is not intended for anyone under 18, and we do not knowingly collect their data.</p>

      <h2>11. Changes and contact</h2>
      <p>
        We may update this policy and will post the new date above. For any privacy question or request,
        contact <a href="mailto:getjeweldesigns@gmail.com">getjeweldesigns@gmail.com</a> or write to us at{" "}
        <strong>Surat, Gujarat, India</strong>.
      </p>
    </LegalLayout>
  );
}
